// Créditos SMS (/creditos-sms) — saldo, recargas e consumo da empresa.
// Mockup: docs/design/aeros-dark/mockups/Tela Aeros Dark - Créditos SMS.dc.html
import React, { useCallback, useEffect, useMemo, useState } from "react";
import Button from "@material-ui/core/Button";
import Tooltip from "@material-ui/core/Tooltip";
import SmsIcon from "@material-ui/icons/Sms";
import AddShoppingCartIcon from "@material-ui/icons/AddShoppingCart";
import NotificationsActiveIcon from "@material-ui/icons/NotificationsActive";
import BoltIcon from "@material-ui/icons/FlashOn";
import moment from "moment";
import { useTheme, alpha } from "@material-ui/core/styles";

import MainContainer from "../../components/MainContainer";
import ComprarCreditosSmsModal from "../../components/ComprarCreditosSmsModal";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import useCampaignPageStyles from "../Campaigns/campaignPageStyles";
import useCreditosSmsPageStyles from "./creditosSmsPageStyles";
import { AEROS, stateText } from "../../styles/aerosTokens";
import {
  barrasConsumo,
  diasRestantes,
  filtraRecargas,
  formatBRL,
  formatMilhar,
  investidoNoMes,
  linhasOrigem,
  precisaAlerta,
  recargasNoMes,
  rotuloDia,
  statusLabel,
  statusTone,
  STATUS_RECARGA,
} from "./creditosSmsRules";

const TOM = {
  success: { cor: AEROS.success, key: "success" },
  warning: { cor: AEROS.warning, key: "warning" },
  neutral: { cor: "#9CA3B8", key: "neutral" },
};

const CORES_ORIGEM = {
  campanha: AEROS.defaultPrimary,
  manual: AEROS.cyan,
  automacao: AEROS.success,
  outros: "#9CA3B8",
};

const CreditosSms = () => {
  const c = useCampaignPageStyles();
  const classes = useCreditosSmsPageStyles();
  const theme = useTheme();

  const [saldo, setSaldo] = useState(null);
  const [recargas, setRecargas] = useState([]);
  const [consumo, setConsumo] = useState(null);
  const [filtro, setFiltro] = useState("all");
  const [comprarAberto, setComprarAberto] = useState(false);
  const [carregando, setCarregando] = useState(true);

  const carrega = useCallback(async () => {
    setCarregando(true);
    try {
      const [saldoResp, recargasResp] = await Promise.all([
        api.get("/sms/saldo"),
        api.get("/sms/recargas"),
      ]);
      setSaldo(saldoResp.data?.saldo ?? null);
      setRecargas(recargasResp.data || []);
    } catch (err) {
      toastError(err);
    }

    // O consumo é complementar: se falhar, a tela continua mostrando saldo e
    // recargas em vez de virar uma página de erro.
    try {
      const { data } = await api.get("/sms/consumo");
      setConsumo(data || null);
    } catch (err) {
      setConsumo(null);
    }
    setCarregando(false);
  }, []);

  useEffect(() => {
    carrega();
  }, [carrega]);

  const visiveis = useMemo(
    () => filtraRecargas(recargas, filtro),
    [recargas, filtro]
  );
  const barras = useMemo(
    () => barrasConsumo(consumo?.porDia || []),
    [consumo]
  );
  const origens = useMemo(
    () => linhasOrigem(consumo?.porOrigem || {}),
    [consumo]
  );

  const mediaDiaria = consumo?.mediaDiaria || 0;
  const dias = diasRestantes(saldo, mediaDiaria);
  const alerta = precisaAlerta(saldo, mediaDiaria);
  const investido = investidoNoMes(recargas);
  const qtdRecargas = recargasNoMes(recargas);

  return (
    <MainContainer>
      <ComprarCreditosSmsModal
        open={comprarAberto}
        onClose={() => setComprarAberto(false)}
        onSaldoAtualizado={carrega}
      />
      <div className={c.pageHeader}>
        <div>
          <div className={c.pageTitle}>{i18n.t("aeros.sms.title")}</div>
          <div className={classes.headerSubtitle}>
            {i18n.t("aeros.sms.subtitle")}
          </div>
        </div>
        <div className={c.headerActions}>
          {/* O "Extrato completo" do mockup não entrou: só existe endpoint de
              extrato para SUPER ADMIN (/sms/admin/extrato/:companyId). A
              empresa não tem como listar os próprios lançamentos ainda —
              botão sem destino é pior que botão ausente. */}
          <Button
            variant="contained"
            color="primary"
            className={c.gradientButton}
            startIcon={<AddShoppingCartIcon />}
            onClick={() => setComprarAberto(true)}
          >
            {i18n.t("aeros.sms.buyCredits")}
          </Button>
        </div>
      </div>

      <div className={c.pageBody}>
        <div className={classes.topGrid}>
          <div className={classes.saldoCard}>
            <div className={classes.saldoLabel}>
              {i18n.t("aeros.sms.currentBalance")}
            </div>
            <div className={classes.saldoRow}>
              <span className={classes.saldoValue}>
                {saldo === null ? "—" : formatMilhar(saldo)}
              </span>
              <span className={classes.saldoUnit}>SMS</span>
            </div>
            <div className={classes.saldoHint}>
              {mediaDiaria > 0
                ? i18n.t("aeros.sms.avgPerDay", {
                    count: Math.round(mediaDiaria),
                  })
                : i18n.t("aeros.sms.noUsageYet")}
            </div>
          </div>

          <div className={classes.kpiCard}>
            <div className={classes.kpiLabel}>{i18n.t("aeros.sms.sentMonth")}</div>
            <div className={classes.kpiValue}>
              {formatMilhar(consumo?.noMes || 0)}
            </div>
            <div className={classes.kpiHint}>
              {mediaDiaria > 0
                ? i18n.t("aeros.sms.avgPerDayShort", {
                    count: Math.round(mediaDiaria),
                  })
                : i18n.t("aeros.sms.noneThisMonth")}
            </div>
          </div>

          <div className={classes.kpiCard}>
            <div className={classes.kpiLabel}>
              {i18n.t("aeros.sms.investedMonth")}
            </div>
            <div className={`${classes.kpiValue} ${classes.kpiInfo}`}>
              {formatBRL(investido, 0)}
            </div>
            <div className={classes.kpiHint}>
              {i18n.t(
                qtdRecargas === 1
                  ? "aeros.sms.rechargeOne"
                  : "aeros.sms.rechargeCount",
                { count: qtdRecargas }
              )}
            </div>
          </div>

          <div className={classes.kpiCard}>
            <div className={classes.kpiLabel}>
              {i18n.t("aeros.sms.autonomy")}
            </div>
            <div className={`${classes.kpiValue} ${classes.kpiSuccess}`}>
              {dias === null
                ? "—"
                : i18n.t(dias === 1 ? "aeros.sms.dayOne" : "aeros.sms.days", {
                    count: dias,
                  })}
            </div>
            <div className={classes.kpiHint}>
              {dias === null
                ? i18n.t("aeros.sms.autonomyUnknown")
                : i18n.t("aeros.sms.autonomyHint")}
            </div>
          </div>
        </div>

        {alerta && (
          <div className={classes.alerta}>
            <NotificationsActiveIcon />
            <div className={classes.alertaTexto}>
              {i18n.t("aeros.sms.lowBalance", { count: dias })}
            </div>
            <Button
              className={classes.alertaBotao}
              onClick={() => setComprarAberto(true)}
            >
              {i18n.t("aeros.sms.rechargeNow")}
            </Button>
          </div>
        )}

        <div className={classes.bodyGrid}>
          <div className={classes.card}>
            <div className={classes.cardHead}>
              <div className={classes.cardTitle}>
                {i18n.t("aeros.sms.recharges")}
              </div>
              <div className={classes.cardSub}>
                {i18n.t("aeros.sms.viaMercadoPago")}
              </div>
              <div className={classes.filtros}>
                {["all", ...STATUS_RECARGA].map((s) => (
                  <Button
                    key={s}
                    className={`${classes.filtro} ${
                      filtro === s ? classes.filtroAtivo : ""
                    }`}
                    onClick={() => setFiltro(s)}
                    aria-pressed={filtro === s}
                  >
                    {s === "all" ? i18n.t("aeros.sms.filterAll") : statusLabel(s)}
                  </Button>
                ))}
              </div>
            </div>

            <div className={classes.rowHead}>
              <div>{i18n.t("aeros.sms.date")}</div>
              <div>{i18n.t("aeros.sms.package")}</div>
              <div style={{ textAlign: "right" }}>
                {i18n.t("aeros.sms.quantity")}
              </div>
              <div style={{ textAlign: "right" }}>{i18n.t("aeros.sms.value")}</div>
              <div style={{ textAlign: "right" }}>
                {i18n.t("aeros.sms.statusColumn")}
              </div>
            </div>

            {carregando ? (
              <div className={classes.vazio}>{i18n.t("aeros.sms.loading")}</div>
            ) : visiveis.length === 0 ? (
              <div className={classes.vazio}>
                {recargas.length === 0
                  ? i18n.t("aeros.sms.noRecharges")
                  : i18n.t("aeros.sms.noRechargesFilter")}
              </div>
            ) : (
              visiveis.map((r) => {
                const tom = TOM[statusTone(r.status)] || TOM.neutral;
                return (
                  <div className={classes.row} key={r.id}>
                    <div className={classes.cellDate}>
                      {moment(r.createdAt).format("DD/MM/YYYY HH:mm")}
                    </div>
                    <div className={classes.cellPack}>
                      <SmsIcon className={classes.packIcon} />
                      <span
                        className={classes.packName}
                        title={r.smsPackage?.nome}
                      >
                        {r.smsPackage?.nome || "—"}
                      </span>
                    </div>
                    <div className={classes.num}>{formatMilhar(r.quantidade)}</div>
                    <div className={classes.numMuted}>{formatBRL(r.valor)}</div>
                    <div className={classes.cellStatus}>
                      <span
                        className={classes.pill}
                        style={{
                          color: stateText(theme, tom.cor, tom.key),
                          backgroundColor: alpha(tom.cor, 0.12),
                        }}
                      >
                        <span
                          className={classes.pillDot}
                          style={{ backgroundColor: tom.cor }}
                        />
                        {statusLabel(r.status)}
                      </span>
                    </div>
                  </div>
                );
              })
            )}

            <div className={classes.rodape}>
              <BoltIcon />
              {i18n.t("aeros.sms.webhookNote")}
            </div>
          </div>

          <div className={classes.lateral}>
            <div className={`${classes.card} ${classes.cardPad}`}>
              <div className={classes.cardTitle}>
                {i18n.t("aeros.sms.usage7days")}
              </div>
              <div className={classes.grafico}>
                {barras.length === 0 ? (
                  <div className={classes.vazio} style={{ width: "100%" }}>
                    {i18n.t("aeros.sms.noUsageData")}
                  </div>
                ) : (
                  barras.map((b) => (
                    <div className={classes.graficoCol} key={b.dia}>
                      <div className={classes.graficoValor}>{b.valor}</div>
                      <div className={classes.graficoTrilho}>
                        <Tooltip title={`${b.valor} SMS`}>
                          <div
                            className={`${classes.graficoBarra} ${
                              b.destaque ? classes.graficoBarraPico : ""
                            }`}
                            style={{ height: `${b.pct}%` }}
                          />
                        </Tooltip>
                      </div>
                      <div className={classes.graficoDia}>{rotuloDia(b.dia)}</div>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className={`${classes.card} ${classes.cardPad}`}>
              <div className={classes.cardTitle}>
                {i18n.t("aeros.sms.whereUsed")}
              </div>
              {origens.length === 0 ? (
                <div className={classes.nota}>
                  {i18n.t("aeros.sms.noUsageData")}
                </div>
              ) : (
                origens.map((o) => (
                  <div className={classes.origemLinha} key={o.chave}>
                    <div className={classes.origemRow}>
                      <span className={classes.origemLabel}>{o.label}</span>
                      <span className={classes.origemValor}>
                        {formatMilhar(o.valor)}
                      </span>
                    </div>
                    <div className={classes.track}>
                      <div
                        className={classes.trackFill}
                        style={{
                          width: `${o.pct}%`,
                          backgroundColor: CORES_ORIGEM[o.chave],
                        }}
                      />
                    </div>
                  </div>
                ))
              )}
              <div className={classes.nota}>
                {i18n.t("aeros.sms.fractionNote")}
              </div>
            </div>
          </div>
        </div>
      </div>
    </MainContainer>
  );
};

export default CreditosSms;
